import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import DataGrid, { Column, Editing } from 'devextreme-react/data-grid';
import SplitterLayout from 'react-splitter-layout';
import { Button, LoadIndicator, SelectBox } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';
import semiDataGrid from './semiDataGrid';
import DataGridMVP from '../../DataGridMVP';
import HeaderButtonMVP from '../../HeaderButtonMVP';
import PopupMVP from '../../PopupMVP';
import styles from './undercutInputTab.module.scss';
import fillUndecutDataGrid from './fillUndecutDataGrid';
import useWasteColumns from '../../../hooks/useWasteColumns';

const defaultDataSource = {
  selectedSemi: [],
  selectedUndercut: null,
  undercutListToFill: null,
  isValid: true
};

const UndercutInputTab = ({
  data,
  loading,
  getUndercutOperation,
  saveUndercut,
  deleteUndercut,
  plantId,
  getUndercutList,
  getSemiProducts,
  undercutReducer,
  userReducer: { id },
  tabsReducer: { tabSelect },
  getUndercutTypesList,
  modalErrorOpen
}) => {
  const [dataSource, setDataSource] = useState(defaultDataSource);
  const [filledData, setFilledData] = useState(null);
  const [visibleModalInput, setVisibleModalInput] = useState(false);
  const [visibleDeleteConfirmation, setVisibleDeleteConfirmation] = useState(
    false
  );
  const [semiFilter, setSemiFilter] = useState({
    dateTo: '',
    dateFrom: ''
  });
  const [headerButtonSemiVisible, setHeaderButtonSemiVisible] = useState({
    display: 'none'
  });
  const [withWasteData, renderWasteCols] = useWasteColumns(data);

  const editableDataGridRef = useRef(null);

  // HELPERS
  const generateFillingData = useCallback((value) => {
    setFilledData(null);
    return (
      value
      && value.cuttings
        .map((cutting) =>
          Array.from({ length: cutting.numberLines }, (v) => ({
            ...cutting,
            weight: null,
            numberLine: null
          })))
        .flat()
    );
  }, []);

  // HANDLERS
  const onSelectedSemiChanged = useCallback(
    ({ selectedRowsData }) => {
      setDataSource({ ...dataSource, selectedSemi: selectedRowsData });
      if (selectedRowsData.length) {
        setHeaderButtonSemiVisible({ display: 'flex' });
      } else {
        setHeaderButtonSemiVisible({ display: 'none' });
      }
    },
    [dataSource]
  );

  const onModalHiding = useCallback(() => {
    setVisibleModalInput(false);
    setFilledData(null);
    setVisibleDeleteConfirmation(false);
    setDataSource((state) => ({
      ...state,
      undercutListToFill: null,
      isValid: false,
      selectedUndercut: null
    }));
  }, []);

  const isDataFilledCorrect = useCallback(
    () =>
      !(
        filledData
        && !!filledData.filter((el) => !el.weight || !el.numberLine).length
      ),
    [filledData]
  );

  const onEditableGridEditorPreparing = useCallback(
    (e) => {
      e.editorOptions.valueChangeEvent = 'input';
      // Отключение изменение числа редактируемой ячейки при нажатии на стрелки
      // переход на другую ячейку при нажатии стрелок
      if (e.editorName === 'dxNumberBox') {
        e.editorOptions.step = 0;
        e.editorOptions.onKeyDown = (arg) => {
          const rowIndex = e.row.rowIndex;
          const columnIndex = e.visibleIndex;
          if (
            arg.event.code === 'ArrowDown'
            && rowIndex < e.component.totalCount()
          ) {
            e.component.editCell(rowIndex + 1, columnIndex);
            e.component.focus(
              e.component.getCellElement(rowIndex + 1, columnIndex)
            );
          }
          if (arg.event.code === 'ArrowUp' && rowIndex > 0) {
            e.component.editCell(rowIndex - 1, columnIndex);
            e.component.focus(
              e.component.getCellElement(rowIndex - 1, columnIndex)
            );
          }
        };
      }
      e.editorOptions.onValueChanged = (args) => {
        e.setValue(args.value);
        const editedData = [...dataSource.undercutListToFill];
        editedData[e.row.dataIndex] = e.row.data;
        setFilledData(editedData);
        if (editableDataGridRef) {
          editableDataGridRef.current.instance
            .getController('validating')
            .validate(true);
        }
      };
    },
    [dataSource.undercutListToFill]
  );

  const onModalSave = useCallback(() => {
    if (!isDataFilledCorrect()) {
      modalErrorOpen({
        message: 'Не все строки подкроя заполнены',
        techInfo: '',
        types: 'error',
        title: formatMessage('textError').warning
      });
    } else if (
      dataSource.selectedUndercut
      && !undercutReducer.undercutOperation
    ) {
      saveUndercut({
        cuttingSize: dataSource.selectedUndercut.cuttingSize,
        semiId: dataSource.selectedSemi[0].pieceNumId,
        cuttings: filledData
      });
    } else if (undercutReducer.undercutOperation) {
      saveUndercut({
        ...undercutReducer.undercutOperation,
        cuttings: filledData || dataSource.undercutListToFill
      });
    }
  }, [
    dataSource.selectedSemi,
    dataSource.selectedUndercut,
    dataSource.undercutListToFill,
    filledData,
    isDataFilledCorrect,
    modalErrorOpen,
    saveUndercut,
    undercutReducer.undercutOperation
  ]);

  const onValueChanged = useCallback(
    ({ value }) => {
      setDataSource({
        ...dataSource,
        undercutListToFill: generateFillingData(value),
        selectedUndercut: value
      });
    },
    [dataSource, generateFillingData]
  );

  const onOpenModalInput = useCallback(() => {
    setVisibleModalInput(true);
    const selectedSemiId = dataSource.selectedSemi[0].pieceNumId;
    getUndercutTypesList({ semiId: selectedSemiId });
    getUndercutOperation({ semiId: selectedSemiId });
  }, [dataSource.selectedSemi, getUndercutOperation, getUndercutTypesList]);

  // EFFECTS
  useEffect(() => {
    if (dataSource.selectedSemi && dataSource.selectedSemi.length === 1) {
      getUndercutList({ semiId: dataSource.selectedSemi[0].pieceNumId });
    }
  }, [
    dataSource.selectedSemi,
    getUndercutList,
    undercutReducer.undercutSave.okRequest,
    undercutReducer.undercutDelete.okRequest
  ]);

  useEffect(() => {
    if (visibleModalInput && undercutReducer.undercutOperation) {
      setDataSource((state) => ({
        ...state,
        undercutListToFill: undercutReducer.undercutOperation.cuttings,
        selectedUndercut: undercutReducer.undercutOperation
      }));
    }
  }, [undercutReducer.undercutOperation, visibleModalInput]);

  useEffect(() => {
    if (
      undercutReducer.undercutSave.okRequest
      || undercutReducer.undercutDelete.okRequest
    ) {
      onModalHiding();
      setDataSource((state) => ({ ...state, undercutListToFill: null }));
    }
  }, [
    onModalHiding,
    undercutReducer.undercutDelete.okRequest,
    undercutReducer.undercutSave.okRequest
  ]);

  useEffect(() => {
    if (id !== null && tabSelect === 8) {
      getSemiProducts({
        ...semiFilter,
        plantId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    undercutReducer.undercutSave.okRequest,
    undercutReducer.undercutDelete.okRequest
  ]);

  // TABLES
  const editableDataGrid = useMemo(
    () => (
      <DataGrid
        dataSource={dataSource.undercutListToFill}
        showBorders
        className={styles.modalTableEditable}
        columnAutoWidth
        onRowValidating={({ isValid }) => {
          setDataSource({ ...dataSource, isValid });
        }}
        ref={editableDataGridRef}
        onEditorPreparing={onEditableGridEditorPreparing}
      >
        <Editing allowUpdating mode="cell" />
        {fillUndecutDataGrid.map((item) => (
          <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch={false}
            selectionMode={false}
            allowFiltering={false}
            allowEditing={item.allowEditing}
            cellRender={item.cellRender}
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          >
            {item.validationRule}
          </Column>
        ))}
      </DataGrid>
    ),
    [dataSource, onEditableGridEditorPreparing]
  );

  const semiTable = useMemo(
    () => (
      <DataGridMVP
        setStateStoringName={'dataGridUndercutSemiIn'}
        keyExpr="pieceNumId"
        setDataGridInstance={() => {}}
        filterDataGrid={semiFilter}
        setFilterDataGrid={setSemiFilter}
        visibleButtonDateRangePicker={false}
        onSelectionChanged={onSelectedSemiChanged}
        visibleInfoIcon={false}
        dataSource={withWasteData}
        className={styles.undercutMainTable}
        loadingData={loading}
      >
        {semiDataGrid.map((item) => (
          <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch
            allowFiltering
            cellRender={item.cellRender}
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          />
        ))}
        {renderWasteCols}
      </DataGridMVP>
    ),
    [loading, onSelectedSemiChanged, semiFilter, withWasteData, renderWasteCols]
  );

  const undercutTable = useMemo(
    () => (
      <DataGridMVP
        setStateStoringName={'dataGridUndercutInput'}
        keyExpr="pieceNumId"
        setDataGridInstance={() => {}}
        visibleButtonDateRangePicker={false}
        visibleInfoIcon={false}
        className={styles.undercutMainTable}
        dataSource={undercutReducer.undercutList}
        loadingData={undercutReducer.undercutGet.loading}
        selectionMode={false}
        filterDataGrid
      >
        {semiDataGrid.map((item) => (
          <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch={false}
            selectionMode={false}
            allowFiltering={false}
            cellRender={item.cellRender}
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          />
        ))}
      </DataGridMVP>
    ),
    [undercutReducer.undercutGet.loading, undercutReducer.undercutList]
  );

  const popupSelectedSemiTable = useMemo(
    () => (
      <DataGrid
        dataSource={dataSource.selectedSemi}
        showBorders
        className={styles.modalTable}
        columnAutoWidth
      >
        {semiDataGrid.map((item) => (
          <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch={false}
            selectionMode={false}
            allowFiltering={false}
            cellRender={item.cellRender}
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          />
        ))}
      </DataGrid>
    ),
    [dataSource.selectedSemi]
  );

  const deleteConfirmationPopup = useMemo(
    () => (
      <PopupMVP
        visible={visibleDeleteConfirmation}
        onHiding={onModalHiding}
        title={formatMessage('textButton').remove}
        width={'90%'}
        height={'auto'}
      >
        <div className="formData">
          <span className={styles.modalLabel}>
            {formatMessage('undercutTab').deleteUndercutConfirm}
          </span>
          <div className="formButton">
            <Button
              type="danger"
              stylingMode="contained"
              onClick={() => {
                deleteUndercut(
                  dataSource.selectedSemi.map((el) => el.pieceNumId)
                );
              }}
              width={260}
            >
              <LoadIndicator
                className="indicator"
                visible={undercutReducer.undercutDelete.loading}
              />
              <span>{formatMessage('textButton').remove}</span>
            </Button>
            <Button
              text={formatMessage('textButton').reject}
              type="default"
              stylingMode="text"
              onClick={onModalHiding}
            />
          </div>
        </div>
      </PopupMVP>
    ),
    [
      dataSource.selectedSemi,
      deleteUndercut,
      onModalHiding,
      undercutReducer.undercutDelete.loading,
      visibleDeleteConfirmation
    ]
  );

  return (
    <>
      <SplitterLayout>
        <div className={styles.dataGridWrapper}>
          <HeaderButtonMVP headButtVisibol={headerButtonSemiVisible}>
            <Button
              text={formatMessage('textButton').remove}
              type={'danger'}
              stylingMode={'contained'}
              onClick={() => {
                setVisibleDeleteConfirmation(true);
              }}
            />
            <Button
              text={formatMessage('textButton').inputUndercut}
              type={'default'}
              stylingMode={'contained'}
              onClick={onOpenModalInput}
              disabled={dataSource.selectedSemi.length !== 1}
            />
          </HeaderButtonMVP>
          {semiTable}
        </div>
        <div className={styles.dataGridWrapper}>{undercutTable}</div>
      </SplitterLayout>
      <PopupMVP
        visible={visibleModalInput}
        onHiding={onModalHiding}
        title={formatMessage('textButton').inputUndercut}
        position={{ offset: '0 -100px 0' }}
        width={'90%'}
        height={'auto'}
      >
        <div className="formData">
          <span className={styles.modalLabel}>
            {' '}
            {formatMessage('undercutTab').selectedUndercut}
            :
          </span>
          {popupSelectedSemiTable}
          <div className={styles.wrapHorizontal}>
            <div className={styles.leftHorizontal}>
              <span className={styles.modalLabel}>
                {' '}
                {formatMessage('undercutTab').typeUndercut}
                :
              </span>
              <div className={styles.selectUndercut}>
                {undercutReducer.undercutTypesListGet.loading
                || undercutReducer.undercutOperationGet.loading ? (
                  <LoadIndicator />
                  ) : (
                    <SelectBox
                    stylingMode="filled"
                    placeholder={`${
                      formatMessage('undercutTab').selectUndercutType
                    }...`}
                    dataSource={
                      undercutReducer.undercutOperation
                        ? [undercutReducer.undercutOperation]
                        : undercutReducer.undercutTypesList
                    }
                    onValueChanged={onValueChanged}
                    width={300}
                    value={
                      undercutReducer.undercutOperation
                        ? undercutReducer.undercutOperation
                        : dataSource.selectedUndercut
                    }
                    readOnly={!!undercutReducer.undercutOperation}
                    deferRendering
                    displayExpr={'cuttingSize'}
                    showClearButton
                    searchEnabled
                  />
                  )}
              </div>
            </div>
            {dataSource.undercutListToFill
              && !undercutReducer.undercutTypesListGet.loading
              && !undercutReducer.undercutOperationGet.loading && (
                <div>
                  <span className={styles.modalLabel}>
                    {formatMessage('undercutTab').cuttings}
                    :
                  </span>
                  {editableDataGrid}
                </div>
            )}
          </div>
          <div className="formButton">
            <Button
              type="default"
              stylingMode="contained"
              onClick={onModalSave}
              disabled={!dataSource.isValid}
              width={260}
            >
              <LoadIndicator
                className="indicator"
                visible={undercutReducer.undercutSave.loading}
              />
              <span>{formatMessage('textButton').save}</span>
            </Button>
            <Button
              text={formatMessage('textButton').reject}
              type="default"
              stylingMode="text"
              onClick={onModalHiding}
            />
          </div>
        </div>
      </PopupMVP>
      {deleteConfirmationPopup}
    </>
  );
};

export default React.memo(UndercutInputTab);
